/** The "Other" specialization; what it means is typed into `specialization_other`. */
export const SPECIALIZATION_OTHER = "OTHER"

export const SPECIALIZATION_OTHER_MAX_LENGTH = 255

export const includesOtherSpecialization = (codes: unknown) =>
  Array.isArray(codes) && codes.includes(SPECIALIZATION_OTHER)

/** CSLB classifications share the specialization list, but CSLB has no "Other". */
export const withoutOtherSpecialization = <T extends object>(
  options?: readonly T[] | null
): T[] =>
  (options ?? []).filter(
    (option) => (option as { value?: unknown }).value !== SPECIALIZATION_OTHER
  )

/**
 * The list returns specializations by name, with Other shown as its typed
 * description. Map them back to codes: the description stands for Other once —
 * and it is checked first, so a description that happens to match a listed name
 * still resolves both.
 */
export const specializationNamesToCodes = (
  names: readonly string[] | null | undefined,
  codeByName: ReadonlyMap<string | undefined, string | undefined>,
  otherDescription?: string | null
): string[] => {
  let otherPending = Boolean(otherDescription)
  return (names ?? []).flatMap((name) => {
    if (otherPending && name === otherDescription) {
      otherPending = false
      return [SPECIALIZATION_OTHER]
    }
    const code = codeByName.get(name)
    return code ? [code] : []
  })
}

/**
 * What a profile shows for its trades: each code by the name the list uses,
 * Other as the description typed for it. A value with no option is kept as is,
 * since some payloads already carry names.
 */
export const specializationDisplayNames = (
  codes: readonly string[],
  options: readonly object[] | null | undefined,
  otherDescription?: string | null
): string[] => {
  const labelByCode = new Map(
    (options ?? []).map((option) => {
      const { value, label } = option as { value?: unknown; label?: unknown }
      return [value, label]
    })
  )
  return codes.map((code) => {
    if (code === SPECIALIZATION_OTHER && otherDescription?.trim()) {
      return otherDescription.trim()
    }
    const label = labelByCode.get(code)
    return typeof label === "string" && label
      ? label
      : code === SPECIALIZATION_OTHER
        ? "Other"
        : code
  })
}
